import React from 'react';
import {Button, Stack, Typography} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {useTranslation} from 'react-i18next';
import {
	useCloseStopsSingleVoyage
} from "@containers/singleVoyage/containers/closeStops/useCloseStopsSingleVoyage";
import {
	hasOwnRadiusSingleVoyage,
	ICloseStopsConflictSingleVoyage,
	MIN_STORED_RADIUS_SINGLE_VOYAGE,
	storedRadiusSingleVoyage
} from "@containers/singleVoyage/containers/closeStops/findCloseStops";
import {IWayPointCreationSingleVoyage} from "@containers/singleVoyage/types/point";
import {useMapFeaturesContext} from "@ui/leafletCustom/context/mapContext/hooks/useMapFeaturesContext";
import {buildZonesBoundsSingleVoyage} from "@containers/singleVoyage/containers/closeStops/closeStopsGeometry";

const sx = {
	root: {
		flexDirection: 'row',
		gap: '8px',
		mx: '10px',
		mb: '10px',
		p: '10px 12px',
		borderRadius: '8px',
		border: '1px solid',
		borderColor: 'error.main',
		bgcolor: 'rgba(211, 47, 47, 0.08)',
	},
	icon: {
		fontSize: '20px',
		color: 'error.main',
		mt: '1px',
	},
	title: {
		fontSize: '13px',
		fontWeight: 600,
	},
	pair: {
		gap: '4px',
	},
	text: {
		fontSize: '12px',
		color: 'text.secondary',
	},
	actions: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: '4px',
	},
	btn: {
		fontSize: '11px',
		lineHeight: 1.4,
		py: '2px',
		px: '8px',
		minWidth: 0,
		textTransform: 'none',
	},
}

export interface ICloseStopsActionsSingleVoyage {
	onRemove: (point: IWayPointCreationSingleVoyage, index: number) => void
	onShow: (point: IWayPointCreationSingleVoyage) => void
	/** Voyage editor: the radius of an ad-hoc stop is the voyage's own and can be changed here. */
	onResize?: (conflict: ICloseStopsConflictSingleVoyage) => void
	/** Template editor: the radius belongs to the stop template, edited in its side panel. */
	onEditStop?: (point: IWayPointCreationSingleVoyage) => void
	disabled?: boolean
}

/** Names the stops that stand inside each other's radius and offers a fix for each pair. */
const CloseStopsNoticeSingleVoyage = ({
	onRemove,
	onShow,
	onResize,
	onEditStop,
	disabled,
}: ICloseStopsActionsSingleVoyage) => {

	const {t} = useTranslation()
	const {conflicts} = useCloseStopsSingleVoyage()
	const {mapFeatures} = useMapFeaturesContext()

	// Selecting the stop zooms to it alone; both zones and their overlap are the point here
	const onShowPair = (conflict: ICloseStopsConflictSingleVoyage) => {
		onShow(conflict.second)
		mapFeatures?.fitBounds(buildZonesBoundsSingleVoyage([conflict.first, conflict.second].map((point) => ({
			centre: point.geom.coordinates,
			radius: storedRadiusSingleVoyage(point.radius),
		}))), {padding: [40, 40]})
	}

	if (!conflicts.length) {
		return <></>
	}

	const renderPair = (conflict: ICloseStopsConflictSingleVoyage) => {
		const canResize = !!onResize
			&& conflict.maxRadius >= MIN_STORED_RADIUS_SINGLE_VOYAGE
			&& hasOwnRadiusSingleVoyage(conflict.first)
			&& hasOwnRadiusSingleVoyage(conflict.second)
		const lockedRadius = !onEditStop && (!hasOwnRadiusSingleVoyage(conflict.first) || !hasOwnRadiusSingleVoyage(conflict.second))
		const stops = [
			{point: conflict.first, number: conflict.firstNumber},
			{point: conflict.second, number: conflict.secondNumber},
		]

		return (
			<Stack
				key={`${conflict.first.id}-${conflict.second.id}`}
				sx={sx.pair}
			>
				<Typography sx={sx.text}>
					{t('singleVoyage.closeStops.pairShort', {
						first: conflict.firstNumber,
						second: conflict.secondNumber,
						distance: conflict.distance,
						radius: conflict.radius,
					})}
				</Typography>

				<Stack sx={sx.actions}>
					{canResize && <Button
                        size={'small'}
                        variant={'contained'}
                        sx={sx.btn}
                        disabled={disabled}
                        onClick={() => onResize?.(conflict)}
                    >
						{t('singleVoyage.closeStops.resize', {radius: conflict.maxRadius})}
                    </Button>}

					{onEditStop && stops.filter(({point}) => !!point.waypointTemplateId).map(({point, number}) => (
						<Button
							key={`edit-${point.id}`}
							size={'small'}
							variant={'outlined'}
							sx={sx.btn}
							disabled={disabled}
							onClick={() => onEditStop(point)}
						>
							{t('singleVoyage.closeStops.editStop', {number})}
						</Button>
					))}

					{stops.map(({point, number}) => (
						<Button
							key={`remove-${point.id}`}
							size={'small'}
							variant={'outlined'}
							color={'error'}
							sx={sx.btn}
							disabled={disabled}
							onClick={() => onRemove(point, number - 1)}
						>
							{t('singleVoyage.closeStops.remove', {number})}
						</Button>
					))}

					<Button
						size={'small'}
						variant={'text'}
						sx={sx.btn}
						onClick={() => onShowPair(conflict)}
					>
						{t('singleVoyage.closeStops.show')}
					</Button>
				</Stack>

				{conflict.maxRadius < MIN_STORED_RADIUS_SINGLE_VOYAGE ? <Typography sx={sx.text}>
					{t('singleVoyage.closeStops.fixMerge', {radius: MIN_STORED_RADIUS_SINGLE_VOYAGE})}
                </Typography> : lockedRadius && <Typography sx={sx.text}>
					{t('singleVoyage.closeStops.lockedRadius')}
                </Typography>}
			</Stack>
		)
	}

	return (
		<Stack sx={sx.root}>
			<WarningAmberIcon sx={sx.icon}/>

			<Stack gap={'8px'}>
				<Typography sx={sx.title}>
					{t('singleVoyage.closeStops.title')}
				</Typography>

				{conflicts.map(renderPair)}

				<Typography sx={sx.text}>
					{t('singleVoyage.closeStops.whyShort')}
				</Typography>
			</Stack>
		</Stack>
	);
};

export default CloseStopsNoticeSingleVoyage;
